import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Buildings, Plus } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

export default function DepartmentManagement() {
  const { user } = useAuth();
  const canMutate = (user?.ux_tier || 0) >= 5;
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const [newD, setNewD] = useState({ name: "", description: "", color: "#263776" });

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const { data } = await api.get("/admin/departments");
      setDepartments(data.departments || []);
    } catch (e) { setLoadErr(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setErr("");
    try {
      await api.post("/admin/departments", newD);
      setNewD({ name: "", description: "", color: "#263776" });
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Create failed."); }
  };

  const remove = async (department_id) => {
    if (!window.confirm("Delete this department? Members must be reassigned first.")) return;
    try {
      await api.delete(`/admin/departments/${department_id}`, { data: { reason: "ui-deletion" } });
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Delete failed."); }
  };

  return (
    <div data-testid="departments-page" className="p-6 max-w-5xl space-y-5">
      {loadErr && <LoadFailedPanel error={loadErr} resource="Department Management" onRetry={load} />}
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <Buildings size={28} className="text-[#F47D20]" />
        Department Management
      </h1>
      {canMutate && (
        <div data-testid="new-dept-form" className="bg-white p-4 border border-[var(--border)] rounded grid grid-cols-12 gap-3 items-end">
          <div className="col-span-4">
            <label className="block text-xs text-[var(--text-muted)] mb-1">Name</label>
            <input data-testid="new-dept-name" value={newD.name} onChange={e => setNewD({...newD, name: e.target.value})}
              className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-5">
            <label className="block text-xs text-[var(--text-muted)] mb-1">Description</label>
            <input data-testid="new-dept-desc" value={newD.description} onChange={e => setNewD({...newD, description: e.target.value})}
              className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-[var(--text-muted)] mb-1">Color</label>
            <input data-testid="new-dept-color" type="color" value={newD.color}
              onChange={e => setNewD({...newD, color: e.target.value})}
              className="w-full border border-[var(--border-strong)] rounded h-9" />
          </div>
          <div className="col-span-1">
            <button data-testid="new-dept-create" onClick={create} disabled={!newD.name}
              className="w-full bg-[#F47D20] text-white rounded py-2 text-sm disabled:opacity-40">
              <Plus weight="bold" />
            </button>
          </div>
        </div>
      )}
      {err && <div data-testid="dept-err" className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded">{err}</div>}
      <div className="bg-white border border-[var(--border)] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] text-[var(--text-secondary)]">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Members</th>
              <th className="text-left px-3 py-2">Color</th>
              {canMutate && <th className="text-right px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-3 py-6 text-[var(--text-faint)] text-center">Loading…</td></tr>
              : departments.map(d => (
                <tr key={d.department_id} data-testid={`dept-row-${d.department_id}`} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 font-medium">{d.name}</td>
                  <td className="px-3 py-2 text-[var(--text-muted)]">{d.description || "—"}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-secondary)] text-xs">{d.member_count}</span></td>
                  <td className="px-3 py-2"><span style={{background: d.color}} className="inline-block w-6 h-4 rounded" /></td>
                  {canMutate && (
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`dept-delete-${d.department_id}`} onClick={() => remove(d.department_id)}
                        className="text-rose-600 hover:underline text-xs">Delete</button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
